import analytics from '../../connectors/analytics';
import messages from '../../constants/logMessages.json';
import { engagementTrackingInit } from '../../lib/engagementTracking';
import { AnalyticsConfig, AnalyticsMfeEventsType } from '../../types';
import logging, { getMessage } from '../logging/index';
import analyticsEventBus from '../mfeEventBus/analyticsEventBus';
import { loadRumScript } from './dynatraceHelpers';

const { errorHeading, errorBold, warnHeading, warnBold, warnRegular } =
  messages;

import { getConfig, setConfig } from '../../configs';
import { targetOriginDefault } from '../../constants';
import {
  getTargetOriginName,
  getTargetOriginUrl,
  isStandalone,
} from './utilHelpers';

const setOriginInConfig = (shellOrigin: string) => {
  setConfig({
    qalMfeConfig: {
      ...getConfig().qalMfeConfig,
      shellOrigin,
    },
  });
  return shellOrigin;
};

/**
 * Determines and stores the shell origin from the URL query parameter or window.name.
 * Falls back to a default origin if neither is available, and logs a warning in that case.
 * @returns {string} The resolved shell origin URL.
 */
const getShellOrigin = (): string => {
  const shellOrigin = getConfig()?.qalMfeConfig?.shellOrigin;
  if (shellOrigin && shellOrigin.startsWith('http')) return shellOrigin;

  const targetOriginUrl = getTargetOriginUrl('targetOrigin');
  if (targetOriginUrl) return setOriginInConfig(targetOriginUrl);

  const targetOriginName = getTargetOriginName(window.name);
  if (targetOriginName) return setOriginInConfig(targetOriginName);

  logging.info(
    getMessage('targetOriginDefaultUsed', [targetOriginDefault]),
    warnHeading,
    warnRegular,
    warnBold,
    warnRegular
  );

  return setOriginInConfig(targetOriginDefault);
};

/**
 * Shell-side: Sends an event from the Shell to an MFE.
 * @param {Window} targetWindow - The target MFE window.
 * @param {string} targetOrigin - The target MFE origin.
 * @param {string} topic - The event topic.
 * @param {Record<string, any>} payload - The event payload.
 */
export const sendShellAnalyticsEventToMFE = (
  targetWindow: Window,
  targetOrigin: string,
  topic: string,
  payload: Record<string, any>
): void => {
  if (targetWindow && targetOrigin) {
    targetWindow.postMessage({ topic, payload }, targetOrigin);
  }
};

/**
 * Shell-side: Subscribes to analytics events from MFEs and forwards them to the Shell's analytics module.
 */
const subscribeMfeEventsToShell = (): void => {
  Object.keys(analytics).forEach((method) => {
    if (
      method.startsWith('track') &&
      typeof (analytics as any)[method] === 'function'
    ) {
      analyticsEventBus.subscribe(method, (data: unknown) => {
        (analytics as any)[method](data);
      });
    }
  });
};

/**
 * Finds an iframe element based on its origin.
 * @param {string} origin - The origin to match.
 * @returns {HTMLIFrameElement | undefined} The matching iframe element, if found.
 */
const findIframeByOrigin = (origin: string): HTMLIFrameElement | undefined => {
  return Array.from(document.getElementsByTagName('iframe')).find((iframe) =>
    iframe.src.startsWith(origin)
  );
};

/**
 * Checks if a given origin is trusted.
 * @param {string} origin - The origin to verify.
 * @param {string[] | Array<string[]>} [allowedOrigins] - The list of allowed origins.
 * @returns {boolean} Whether the origin is trusted.
 */
const isTrustedOrigin = (
  origin: string,
  allowedOrigins?: string[]
): boolean => {
  if (
    !origin ||
    !allowedOrigins ||
    !Array.isArray(allowedOrigins) ||
    allowedOrigins.some((o) => typeof o !== 'string' || !o)
  ) {
    logging.error(
      getMessage('targetOriginUndefined', ['undefined']),
      errorHeading,
      errorBold
    );
    return false;
  }

  return (
    allowedOrigins.includes(origin) ||
    origin.endsWith('qbe.com') ||
    origin.endsWith('qbe.com.au')
  );
};

/**
 * Shell-side: Listens for messages from MFEs and publishes them to the Shell's analytics event bus.
 * @param {AnalyticsConfig} config - The analytics configuration object.
 *
 * This function validates the origins of incoming messages against the provided configuration,
 * ensuring only trusted MFEs can communicate. If the origin is trusted and the message contains
 * a valid `method`, the message is published to the analytics event bus with its payload.
 *
 * @param config - The analytics configuration object, which should include a list of allowed MFE origins
 *                 under `qalMfeConfig.mfeOrigins`.
 *
 * @remarks
 * - Logs an error if the MFE origins are not properly defined or invalid.
 * - Ignores messages from untrusted origins or those without a `method` property.
 * - Publishes valid messages to the analytics event bus, including additional MFE metadata.
 */
const listenForMfeMessages = (config: AnalyticsConfig): void => {
  window.addEventListener('message', (event: MessageEvent) => {
    const { mfeOrigins = [] } = config.qalMfeConfig || {};

    if (
      !Array.isArray(mfeOrigins) ||
      mfeOrigins.length === 0 ||
      mfeOrigins.some((origin) => typeof origin !== 'string' || !origin)
    ) {
      logging.error(
        getMessage('targetOriginUndefined', ['undefined']),
        errorHeading,
        errorBold
      );
      return;
    }
    const { method, payload, mfeName, mfeDomain } = event.data ?? {};

    if (!isTrustedOrigin(event.origin, mfeOrigins)) return;
    if (!method) return;

    analyticsEventBus.publish(method, {
      ...payload,
      mfeName,
      method,
      mfeDomain,
    });
  });
};

/**
 * Shell-side: Sends the Shell's analytics context to MFEs upon receiving the 'ANALYTICS_MFE_CONTEXT_READY' message.
 * @param {AnalyticsConfig} config - The analytics configuration object.
 */
const sendShellContextToMfe = (config: AnalyticsConfig): void => {
  const { mfeOrigins, mfeContext } = config.qalMfeConfig || {};
  if (!mfeOrigins || !mfeContext) return;

  window.addEventListener('message', (event: MessageEvent) => {
    if (
      event.data?.topic === AnalyticsMfeEventsType.ANALYTICS_MFE_CONTEXT_READY
    ) {
      const targetIframe = findIframeByOrigin(event.origin);
      if (!targetIframe?.contentWindow) return;

      sendShellAnalyticsEventToMFE(
        targetIframe.contentWindow,
        event.origin,
        AnalyticsMfeEventsType.ANALYTICS_CONTEXT_TO_MFE,
        mfeContext
      );
    }
  });
};

/**
 * Shell-side setup: Listens for MFE messages and subscribes to analytics events.
 * @param {AnalyticsConfig} config - The analytics configuration object.
 */
const setupShellListeners = (config: AnalyticsConfig): void => {
  subscribeMfeEventsToShell();
  listenForMfeMessages(config);
  sendShellContextToMfe(config);
};

/**
 * Sends an analytics event message from an MFE to the Shell via `postMessage`.
 * @param {string} method - The analytics method name.
 * @param {unknown} payload - The event data payload.
 * @param {string} mfeName - The name of the Micro Frontend (MFE).
 */
export const postAnalyticsMessageToShell = (
  method: string,
  payload: unknown,
  mfeName: string
): void => {
  const shellOrigin = getShellOrigin();
  window.parent.postMessage(
    {
      method,
      payload,
      mfeName,
      shellOrigin,
      mfeDomain: `${location.protocol}//${location.host}`,
    },
    shellOrigin
  );
};

/**
 * MFE-side: Subscribes to 'ANALYTICS_CONTEXT_TO_MFE' to receive the analytics context from the Shell.
 */
const subscribeShellContext = (): void => {
  analyticsEventBus.subscribe(
    AnalyticsMfeEventsType.ANALYTICS_CONTEXT_TO_MFE,
    (shellContext: any) => {
      // Logic to load Dynatrace Script into MFE.
      const { enableDynatraceMfe, rumScript, dynatraceScript } =
        shellContext || {};

      const qalMfeConfig = getConfig()?.qalMfeConfig || {};

      if (enableDynatraceMfe && (rumScript || dynatraceScript)) {
        loadRumScript(rumScript);
      }
      // Also copy enableDynatraceMfe from the shellContext to the MFE Config
      setConfig({
        qalMfeConfig: {
          ...qalMfeConfig,
          mfeContext: {
            ...qalMfeConfig.mfeContext,
            enableDynatraceMfe,
          },
        },
      });
    }
  );
};

/**
 * MFE-side: Listens for messages from the Shell and publishes them to the MFE's analytics event bus.
 * @param {AnalyticsConfig} config - The analytics configuration object.
 */
const listenForShellMessages = (
  config: AnalyticsConfig,
  shellOrigin: string
) => {
  window.addEventListener('message', (event: MessageEvent) => {
    if (!shellOrigin) return;
    // Verify the origin of the received message
    if (event.origin !== shellOrigin) {
      logging.error(
        getMessage('targetOriginUntrusted', [shellOrigin, event.origin]),
        errorHeading,
        errorBold
      );
      return;
    }

    const { topic, payload } = event.data ?? {};
    if (!topic) return;

    analyticsEventBus.publish(topic, payload);
  });
};

/**
 * Notifies the Shell that the MFE is ready to receive messages.
 */
const notifyShellMfeIsReady = (shellOrigin: string): void => {
  window.parent.postMessage(
    { topic: 'ANALYTICS_MFE_CONTEXT_READY' },
    shellOrigin
  );
};

/**
 * MFE-side setup: Listens for messages from the Shell and initialises engagement tracking if enabled from Shell > MFE
 * @param {AnalyticsConfig} config - The analytics configuration object.
 */
const setupMfeListeners = (config: AnalyticsConfig): void => {
  const shellOrigin = getShellOrigin();
  subscribeShellContext();

  listenForShellMessages(config, shellOrigin);

  if (config.enableQalInBuiltEngagementTracking) {
    engagementTrackingInit();
  }

  notifyShellMfeIsReady(shellOrigin);
};

/**
 * Initialises event handling for MFE and Shell based on configuration.
 * @param {AnalyticsConfig} config - The analytics configuration object.
 */
export const initialiseMfeEventController = (config: AnalyticsConfig): void => {
  if (!config.enableQalMfe) return;

  const mfeType = config.qalMfeConfig?.mfeType;

  if (mfeType === 'shell') {
    setupShellListeners(config);
  } else if (mfeType === 'mfe') {
    // If the MFE was opened in its own browser and not embedded in a shell, don't setup mfe listeners
    if (isStandalone) return;
    if (!getShellOrigin()) {
      logging.error(getMessage('targetOriginNotSet'), errorHeading, errorBold);
      return;
    }

    setupMfeListeners(config);
  }
};
